import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { successToast, errorToast } from 'utils/toast';
import { deleteDomain, validateDomain } from 'services/DomainService';
import ConfirmModal from 'components/ConfirmModal';
import DomainListItem from './DomainListItem';

import './styles.scss';

function DomainList({ domains, onDomainDeleted, onDomainUpdated }) {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [domainToDelete, setDomainToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteRequest = useCallback((domain) => {
        setDomainToDelete(domain);
        setIsDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        setIsDeleteModalOpen(false);
        setDomainToDelete(null);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        if (!domainToDelete) return;

        setIsDeleting(true);
        deleteDomain(domainToDelete.id)
            .then(() => {
                successToast({
                    text: getLiteral('succes_entitydeletedsuccessfully'),
                });
                onDomainDeleted?.(domainToDelete.id);
                closeDeleteModal();
            })
            .catch(() => {
                errorToast({
                    text: getLiteral('error_generalerror'),
                });
            })
            .finally(() => {
                setIsDeleting(false);
            });
    }, [domainToDelete, onDomainDeleted, closeDeleteModal]);

    const handleUpdateStatus = useCallback(
        (domainId) => {
            return validateDomain(domainId)
                .then((result) => {
                    if (result) {
                        successToast({
                            text: getLiteral('succes_entityupdatedsuccessfully'),
                        });
                        onDomainUpdated?.();
                    }
                })
                .catch(() => {
                    errorToast({
                        text: getLiteral('error_generalerror'),
                    });
                });
        },
        [onDomainUpdated],
    );

    return (
        <>
            <div className="domain-list">
                <div className="domain-list__header">
                    <div className="domain-list__header-grid">
                        <div className="domain-list__header-cell domain-list__header-cell--expand" />
                        <div className="domain-list__header-cell domain-list__header-cell--domain">
                            <Text type="body" bold>
                                {getLiteral('label_marketing_campaigns_domain_name')}
                            </Text>
                        </div>
                        <div className="domain-list__header-cell domain-list__header-cell--status">
                            <Text type="body" bold>
                                {getLiteral('label_status')}
                            </Text>
                        </div>
                        <div className="domain-list__header-cell domain-list__header-cell--actions" />
                    </div>
                </div>
                <div className="domain-list__body">
                    {domains.map((domain) => (
                        <DomainListItem
                            key={domain.id}
                            domain={domain}
                            onDeleteRequest={handleDeleteRequest}
                            onClickUpdateStatus={handleUpdateStatus}
                        />
                    ))}
                </div>
            </div>
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
                title={getLiteral('action_delete')}
                confirmText={getLiteral('action_delete')}
                cancelText={getLiteral('action_cancel')}
                isLoading={isDeleting}
                buttonType="primary-error"
                size="small"
            >
                <Text>{getLiteral('label_marketing_campaigns_delete_domain_message')}</Text>
            </ConfirmModal>
        </>
    );
}

DomainList.propTypes = {
    domains: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            domain: PropTypes.string.isRequired,
            status: PropTypes.number.isRequired,
            objectDomain: PropTypes.array,
        }),
    ),
    onDomainDeleted: PropTypes.func,
    onDomainUpdated: PropTypes.func,
};

DomainList.defaultProps = {
    domains: [],
    onDomainDeleted: null,
    onDomainUpdated: null,
};

DomainList.displayName = 'DomainList';

export default DomainList;
